import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { CalendarClock, ExternalLink, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Switch,
} from '@artstorefronts/ui';
import AdminLayout from '../../../components/AdminLayout';
import {
  ADMIN_BOUNTIES,
  ADMIN_BOUNTY_CATALOG,
  ADMIN_SET_BOUNTY_STATUS,
  ADMIN_TOGGLE_STEP_BOUNTY,
  bountyPageUrl,
  cadenceLabel,
  periodWord,
  channelsLabel,
  isSecret,
  KIND_LABEL,
  kindNoun,
  scheduleLabel,
  stepPanelPreviewUrl,
  type AdminBounty,
  type BountyCatalogEntry,
} from './bounty-shared';
import { ScheduleBountyDialog } from './ScheduleBountyDialog';
import { SeriesReachBackField, canReachBack, reachBackValue, seriesPublishLine } from './SeriesReachBack';
import { BountyMemberLookup } from './BountyMemberLookup';
import { PointEvents } from './PointEvents';
import {
  draftGoingLive,
  payoutLabel,
  pointsGoingLive,
  pointsOnStep,
  publishSelectsLine,
  togglePaysLine,
} from './bounty-name';

type Group = { key: string; title: string; note: string; rows: AdminBounty[] };

/**
 * Groups follow the schedule alone — series, recurring, one-off — so a bounty is where its
 * cadence says, system rows included. Secret, invite-only and system are labels on the row.
 */
function groupBounties(bounties: AdminBounty[]): Group[] {
  const series = bounties.filter((b) => b.seriesKey).sort((a, b) => (a.seriesDay ?? 0) - (b.seriesDay ?? 0));
  const others = bounties.filter((b) => !b.seriesKey);
  return [
    { key: 'series', title: 'Onboarding series', note: 'Days 0–14 from signup. Pays new members only.', rows: series },
    {
      key: 'recurring',
      title: 'Recurring',
      note: 'Pays every time it happens, or comes back each day, week or month.',
      rows: others.filter((b) => b.cadence !== 'once'),
    },
    { key: 'one-off', title: 'One-off', note: 'Once per member.', rows: others.filter((b) => b.cadence === 'once') },
  ];
}

/** The labels a row carries under its name. The series table has no Delivery column, so its rows carry delivery here. */
function bountyTags(bounty: AdminBounty): string[] {
  const tags: string[] = [];
  if (bounty.kind !== 'task') tags.push(KIND_LABEL[bounty.kind]);
  if (bounty.seriesKey) tags.push(channelsLabel(bounty));
  if (bounty.showOnPoints) tags.push('On /points');
  if (!bounty.showOnBounties) tags.push('Unlisted');
  if (bounty.enrollment === 'invite') tags.push('Invite-only');
  if (bounty.enrollment === 'optin') tags.push('Opt-in');
  return tags;
}

/** The series table leads with each bounty's day and leaves Delivery to the editor. */
function gridColumns(groupKey: string): string {
  if (groupKey === 'series') {
    return 'grid-cols-[60px_2fr_70px_150px_90px_70px_240px]';
  }
  return 'grid-cols-[2fr_70px_90px_120px_90px_70px_240px]';
}

/**
 * The far-right column: the live switch, when a draft is scheduled to go live, a schedule
 * button for drafts, and — while it is live and members can see it — a link to its page.
 */
function BountyLiveControls({
  bounty,
  onStatus,
  onSchedule,
}: {
  bounty: AdminBounty;
  onStatus: (status: AdminBounty['status']) => void;
  onSchedule: () => void;
}) {
  const live = bounty.status === 'live';
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Switch
        checked={live}
        onCheckedChange={(on) => onStatus(on ? 'live' : 'draft')}
        aria-label={live ? `Take ${bounty.name} to draft` : `Put ${bounty.name} live`}
        data-testid="bounty-live-switch"
      />
      <span className="text-xs text-gray-600">
        {live ? 'Live' : bounty.status === 'archived' ? 'Archived' : 'Draft'}
      </span>
      {bounty.status === 'draft' ? (
        <Button
          variant="outline"
          size="sm"
          aria-label={bounty.scheduledFor ? 'Reschedule its go-live' : 'Schedule its go-live'}
          title={bounty.scheduledFor ? 'Reschedule' : 'Schedule'}
          onClick={onSchedule}
        >
          <CalendarClock className="h-3 w-3" />
        </Button>
      ) : null}
      {live && !isSecret(bounty) && !bounty.systemKey ? (
        <Button variant="outline" size="sm" asChild>
          <a
            href={bountyPageUrl(bounty.slug)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="View its page"
            title="View its page"
          >
            <ExternalLink className="h-3 w-3" />
          </a>
        </Button>
      ) : null}
      {bounty.scheduledFor ? (
        <span className="basis-full text-xs text-blue-700" data-testid="bounty-scheduled">
          Scheduled · {scheduleLabel(bounty.scheduledFor)}
        </span>
      ) : null}
    </span>
  );
}

export default function AdminBounties() {
  const navigate = useNavigate();
  const { data, loading, refetch } = useQuery<{ adminBounties: AdminBounty[] }>(ADMIN_BOUNTIES, {
    fetchPolicy: 'cache-and-network',
  });
  const { data: catalogData, refetch: refetchCatalog } = useQuery<{ adminBountyCatalog: BountyCatalogEntry[] }>(
    ADMIN_BOUNTY_CATALOG,
    { fetchPolicy: 'cache-and-network' }
  );
  const [setStatus] = useMutation(ADMIN_SET_BOUNTY_STATUS);
  const [toggleStep] = useMutation(ADMIN_TOGGLE_STEP_BOUNTY);
  const [statusTarget, setStatusTarget] = useState<{ bounty: AdminBounty; status: AdminBounty['status'] } | null>(null);
  const [reachBack, setReachBack] = useState('');
  const [scheduleTarget, setScheduleTarget] = useState<AdminBounty | null>(null);
  const [stepTarget, setStepTarget] = useState<{ entry: BountyCatalogEntry; on: boolean } | null>(null);

  const groups = useMemo(() => groupBounties(data?.adminBounties ?? []), [data]);
  const catalog = catalogData?.adminBountyCatalog ?? [];

  const refresh = () => {
    void refetch();
    void refetchCatalog();
  };

  const handleStatus = async (confirmEmailBlast = false) => {
    if (!statusTarget) return;
    try {
      await setStatus({
        variables: {
          id: statusTarget.bounty.id,
          status: statusTarget.status,
          confirmEmailBlast,
          reachBackDays: statusTarget.status === 'live' ? reachBackValue(reachBack) : null,
        },
      });
      toast.success(`"${statusTarget.bounty.name}" is now ${statusTarget.status}.`);
      setStatusTarget(null);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to change status');
    }
  };

  const handleToggle = async () => {
    if (!stepTarget) return;
    try {
      await toggleStep({ variables: { stepCode: stepTarget.entry.code, on: stepTarget.on } });
      toast.success(stepTarget.on ? 'Bounty live.' : 'Bounty archived.');
      setStepTarget(null);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to switch the step');
    }
  };

  const emailsEveryone = (bounty: AdminBounty) =>
    bounty.channelEmail && bounty.enrollment === 'everyone' && !bounty.seriesKey && bounty.cadence !== 'once';

  return (
    <AdminLayout
      title="Bounties"
      description="One step, paid in points. The onboarding series — bounties, messages and artist cards — recurring and secret bounties, and every step as a possible bounty."
      action={
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/bounties/new?kind=message')}>
            <Plus className="mr-2 h-4 w-4" /> New series item
          </Button>
          <Button onClick={() => navigate('/admin/bounties/new')}>
            <Plus className="mr-2 h-4 w-4" /> New bounty
          </Button>
        </div>
      }
    >
      <PointEvents />
      <BountyMemberLookup />

      {loading && !data ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : (
        groups.map((group) =>
          group.key === 'series' && group.rows.length === 0 ? null : (
            <section key={group.key} className="mb-8" data-testid={`bounty-group-${group.key}`}>
              <h2 className="text-base font-semibold text-gray-900">{group.title}</h2>
              <p className="mb-2 text-xs text-gray-500">{group.note}</p>
              <div className="overflow-x-auto">
                <div className="min-w-[900px]">
                  <div
                    className={`grid ${gridColumns(group.key)} items-center gap-2 border-b px-2 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500`}
                  >
                    {group.key === 'series' ? <span>Day</span> : null}
                    <span>Bounty</span>
                    <span>Points</span>
                    {group.key === 'series' ? (
                      <span title="Reached in the inbox · emailed · opened or connected from">Reach</span>
                    ) : (
                      <span>Cadence</span>
                    )}
                    {group.key === 'series' ? null : <span>Delivery</span>}
                    <span>Audience</span>
                    <span>Paid</span>
                    <span>Live</span>
                  </div>
                  {group.rows.length === 0 ? (
                    <p className="border-b px-2 py-3 text-sm text-gray-500">None yet.</p>
                  ) : null}
                  {group.rows.map((bounty) => (
                    <div
                      key={bounty.id}
                      className={`grid ${gridColumns(group.key)} items-center gap-2 border-b px-2 py-3 text-sm`}
                    >
                      {group.key === 'series' ? (
                        <span className="font-medium text-gray-900">Day {bounty.seriesDay}</span>
                      ) : null}
                      <span className="min-w-0">
                        <Link
                          to={`/admin/bounties/${bounty.id}`}
                          className="block truncate font-medium text-blue-600 hover:underline"
                        >
                          {bounty.emoji ? `${bounty.emoji} ` : ''}
                          {bounty.name}
                        </Link>
                        {bounty.scheduleError ? (
                          <span className="mt-0.5 block text-xs text-red-700" title={bounty.scheduleError}>
                            Scheduled go-live failed: {bounty.scheduleError}
                          </span>
                        ) : null}
                        {bountyTags(bounty).length > 0 ? (
                          <span className="mt-0.5 flex flex-wrap gap-1">
                            {bountyTags(bounty).map((tag) => (
                              <span
                                key={tag}
                                className="whitespace-nowrap rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600"
                              >
                                {tag}
                              </span>
                            ))}
                          </span>
                        ) : null}
                      </span>
                      <span className="text-gray-700">{bounty.pointsReward}</span>
                      {group.key === 'series' ? (
                        <span
                          className="text-xs text-gray-600"
                          title="Reached in the inbox · emailed · opened (a message) or connected from (artist cards)"
                          data-testid="bounty-reach"
                        >
                          {bounty.deliveredCount.toLocaleString()} · {bounty.emailedCount.toLocaleString()} ·{' '}
                          {bounty.clickedCount.toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-gray-600">{cadenceLabel(bounty.cadence)}</span>
                      )}
                      {group.key === 'series' ? null : (
                        <span className="text-xs text-gray-600">{channelsLabel(bounty)}</span>
                      )}
                      <span className="text-gray-600" title="Members its audience selects, of all members">
                        {bounty.audienceCount.toLocaleString()}
                      </span>
                      <span
                        className="text-gray-600"
                        title={bounty.systemKey ? 'Payouts since it went live, less any taken back' : undefined}
                      >
                        {/* A no-point message stamps its open as paid; Reach already counts that. */}
                        {bounty.kind !== 'task' && bounty.pointsReward === 0 ? '—' : bounty.paidCount.toLocaleString()}
                      </span>
                      <BountyLiveControls
                        bounty={bounty}
                        onStatus={(status) => setStatusTarget({ bounty, status })}
                        onSchedule={() => setScheduleTarget(bounty)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )
        )
      )}

      <section className="mb-8" data-testid="bounty-all-steps">
        <h2 className="text-base font-semibold text-gray-900">All steps</h2>
        <p className="mb-2 text-xs text-gray-500">
          Every quest step, as a bounty. The points on a row are what that bounty pays. Switching one on publishes it in
          the step&apos;s own words, delivered to the inbox and the band — a new one at the default, a draft at the
          points it was saved with. The confirm names them before it goes live. It opens the same panel the quest band
          does — “Panel” shows it, without completing anything.
        </p>
        <div className="overflow-x-auto">
          <div className="min-w-[860px]">
            {catalog
              .filter((entry) => !entry.isSeriesItemOnly)
              .map((entry) => {
                const blocked =
                  entry.onceVerdict ?? (entry.productionReady ? null : 'Local-only — not live in production.');
                return (
                  <div
                    key={entry.code}
                    className="grid grid-cols-[56px_1.3fr_1.6fr_1fr_70px] items-center gap-3 border-b px-2 py-2 text-sm"
                    data-testid={`bounty-step-${entry.code}`}
                  >
                    <Switch
                      checked={entry.on}
                      disabled={Boolean(blocked) && !entry.on}
                      onCheckedChange={(on) => setStepTarget({ entry, on })}
                      aria-label={`${entry.on ? 'Turn off' : 'Turn on'} the ${entry.adminLabel} bounty`}
                    />
                    <span>
                      <span className="font-medium text-gray-900">{entry.adminLabel}</span>
                      <span className="block text-xs text-gray-700">{payoutLabel(pointsOnStep(entry.bounties))}</span>
                      <span className="block text-xs text-gray-500">{entry.adminMeasurement}</span>
                    </span>
                    <span className="text-gray-700">{entry.defaultLabel}</span>
                    <span className="text-xs text-gray-500">
                      {blocked ? (
                        <span className="text-amber-700">{blocked}</span>
                      ) : entry.bounties.length > 0 ? (
                        entry.bounties.map((b) => (
                          <Link
                            key={b.id}
                            to={`/admin/bounties/${b.id}`}
                            className="mr-2 text-blue-600 hover:underline"
                          >
                            {b.name} ({b.status})
                          </Link>
                        ))
                      ) : (
                        'No bounty yet'
                      )}
                      {entry.recurringVerdict ? null : <span className="ml-1 text-green-700">· can recur</span>}
                    </span>
                    <a
                      className="text-xs font-medium text-indigo-600 hover:underline"
                      href={stepPanelPreviewUrl(entry.code)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Panel →
                    </a>
                  </div>
                );
              })}
          </div>
        </div>
      </section>

      <ScheduleBountyDialog
        bounty={scheduleTarget}
        emailsEveryone={Boolean(scheduleTarget && emailsEveryone(scheduleTarget))}
        onClose={() => setScheduleTarget(null)}
        onScheduled={refresh}
      />

      <AlertDialog
        open={statusTarget !== null}
        onOpenChange={(open) => {
          if (open) return;
          setStatusTarget(null);
          setReachBack('');
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {statusTarget?.status === 'live'
                ? `Publish this ${kindNoun(statusTarget.bounty.kind)}?`
                : `Take this ${kindNoun(statusTarget?.bounty.kind)} to draft?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {statusTarget?.status !== 'live'
                ? 'It stops paying and leaves every band and inbox. Points already paid stay paid.'
                : statusTarget.bounty.seriesKey
                  ? seriesPublishLine(statusTarget.bounty, reachBackValue(reachBack))
                  : publishSelectsLine(statusTarget.bounty.pointsReward, statusTarget.bounty.audienceCount)}
              {statusTarget && statusTarget.status === 'live' && emailsEveryone(statusTarget.bounty)
                ? ` It also emails every recently active member in that audience each ${periodWord(statusTarget.bounty.cadence)}.`
                : ''}
            </AlertDialogDescription>
            {statusTarget?.status === 'live' && canReachBack(statusTarget.bounty) ? (
              <SeriesReachBackField bounty={statusTarget.bounty} value={reachBack} onChange={setReachBack} />
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                void handleStatus(
                  Boolean(statusTarget && statusTarget.status === 'live' && emailsEveryone(statusTarget.bounty))
                )
              }
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={stepTarget !== null} onOpenChange={(open) => !open && setStepTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {stepTarget?.on ? 'Turn this step on as a bounty?' : 'Turn this bounty off?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {stepTarget?.on
                ? togglePaysLine(
                    pointsGoingLive(stepTarget.entry.bounties),
                    stepTarget.entry.defaultLabel,
                    draftGoingLive(stepTarget.entry.bounties)
                  )
                : 'Every live bounty on this step is archived and stops paying. Points already paid stay paid.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleToggle()}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
